"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Button } from "@/components/marketing/ui/Button";
import { EvalResultView } from "@/components/shared/EvalResultView";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { ErrorBanner } from "@/components/shared/ErrorBanner";
import { isPass, weakestRating } from "@/lib/stepped/ratings";
import { matchesAuthoredModel, MODEL_MATCH_PASS } from "@/lib/retryRevealPolicy";
import type { EvalResult } from "@/lib/types";
import { getTaster } from "@/lib/free/tasters";
import { track } from "@/lib/analytics";
import { ANALYTICS_EVENTS } from "@/lib/analyticsEvents";

const SignupGate = ({ heading, blurb, slug }: { heading: string; blurb: string; slug: string }) => (
  <div className="rounded-2xl border border-line bg-paper-2 p-6 text-center shadow-[0_4px_0_var(--gold)]">
    <p className="text-3xl">✍️</p>
    <h3 className="font-display mt-2 text-xl font-semibold">{heading}</h3>
    <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink-soft">{blurb}</p>
    <Button href={`/auth/signup?from=${slug}`} size="sm" className="mt-5">
      Create a free account →
    </Button>
    <p className="mt-3 text-xs text-ink-soft">
      Already have one?{" "}
      <Link href="/auth/login" className="font-semibold underline">
        Log in
      </Link>
    </p>
  </div>
);

// Takes a slug (a serializable string) rather than the config object, because a
// Server Component can't pass the config's `message` functions across the
// client boundary. The config is resolved here, on the client.
export function FreeTaster({ slug }: { slug: string }) {
  const [index, setIndex] = useState(0);
  const [answer, setAnswer] = useState("");
  const [result, setResult] = useState<EvalResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gated, setGated] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const config = getTaster(slug);

  useEffect(() => {
    if (!config) return;
    track(ANALYTICS_EVENTS.TASTER_STARTED, { slug, mode: config.mode });
    // config is a stable reference per slug (a static TASTERS[slug] lookup),
    // so this only re-fires on an actual slug change, not on every render.
  }, [slug, config]);

  if (!config) return null; // the page guarantees this; guard for safety

  const prompt = config.prompts[index];
  const isLast = index === config.prompts.length - 1;
  const rating = result ? weakestRating(result.checks) : null;
  const passed = rating ? isPass(rating) : false;
  const working = !gated && !rateLimited;

  const submit = async () => {
    // If the visitor submits exactly the revealed exemplar (the "Stuck? Show me a
    // strong one" model), pass without calling the grader. This is the public
    // funnel, so the non-deterministic grader wrongly failing a copied suggestion
    // is a signup-costing first impression — never let that contradiction show.
    if (matchesAuthoredModel(answer, [prompt.exemplar].filter(Boolean))) {
      setResult(MODEL_MATCH_PASS);
      track(ANALYTICS_EVENTS.TASTER_COMPLETED, {
        slug,
        mode: config.mode,
        promptIndex: index,
        shortcut: true,
        passed: true,
      });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/coach/free", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: config.mode,
          payload: { message: prompt.message(answer) },
        }),
      });
      // The platform rate-limit (Vercel Firewall) returns a 429 with a non-JSON
      // body, so handle it by status before attempting to parse.
      if (res.status === 429) {
        setRateLimited(true);
        return;
      }
      const data = await res.json();
      if (data?.limitReached) {
        setGated(true);
        return;
      }
      if (!res.ok) throw new Error(data?.error ?? "Something went wrong. Please try again.");
      // The grader occasionally emits malformed JSON (e.g. unescaped quotes), so
      // the server returns structured: null. Surface that as a retryable error
      // rather than silently dropping the result (which looks like a dead button).
      const structured = data.structured as EvalResult | null | undefined;
      if (!structured || !Array.isArray(structured.checks) || structured.checks.length === 0) {
        throw new Error("The coach hiccuped on that one — please try again.");
      }
      setResult(structured);
      track(ANALYTICS_EVENTS.TASTER_COMPLETED, {
        slug,
        mode: config.mode,
        promptIndex: index,
        shortcut: false,
        passed: isPass(weakestRating(structured.checks)),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Retry the SAME prompt.
  const tryAgain = () => {
    setResult(null);
    setAnswer("");
    setRevealed(false);
    setRateLimited(false);
  };

  // Advance to the NEXT prompt after a pass.
  const nextPrompt = () => {
    setIndex((i) => i + 1);
    setResult(null);
    setAnswer("");
    setRevealed(false);
  };

  return (
    <div className="mx-auto max-w-2xl px-6 py-10">
      <p className="text-xs font-bold tracking-wide text-ink-soft uppercase">
        Free sample · no sign-up needed
      </p>
      <h1 className="font-display mt-1 text-3xl font-semibold tracking-tight sm:text-4xl">
        {config.title}
      </h1>
      <p className="mt-3 text-base leading-relaxed text-ink-soft">{config.intro}</p>

      <div className="mt-8 space-y-4">
        {working && (
          <>
            <p className="text-xs font-semibold tracking-wide text-ink-soft uppercase">
              Prompt {index + 1} of {config.prompts.length}
            </p>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                {prompt.sourceLabel}
              </p>
              <p className="mt-1 font-mono whitespace-pre-line text-gray-800">{prompt.source}</p>
              {prompt.hints.map((h) => (
                <p key={h.label} className="mt-2 text-xs text-gray-500">
                  {h.label}: <span className="font-medium text-gray-700">{h.value}</span>
                </p>
              ))}
            </div>
          </>
        )}

        <ErrorBanner error={error} />
        {loading && <LoadingSpinner />}

        {!loading && !result && working && (
          <>
            <textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder={config.placeholder}
              className="min-h-[120px] w-full rounded-xl border-2 border-gray-200 p-4 text-base leading-relaxed focus:border-emerald-400 focus:outline-none"
            />
            <div className="flex items-center gap-4">
              <button
                onClick={submit}
                disabled={!answer.trim()}
                className="rounded-lg bg-emerald-500 px-5 py-2 font-semibold text-white hover:bg-emerald-600 disabled:opacity-40"
              >
                Check my writing
              </button>
              {!revealed && (
                <button
                  onClick={() => setRevealed(true)}
                  className="text-xs font-medium text-gray-500 hover:text-gray-800 hover:underline"
                >
                  Stuck? Show me a strong one
                </button>
              )}
            </div>
            {revealed && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-xs font-semibold tracking-wide text-emerald-700 uppercase">
                  One strong version
                </p>
                <p className="mt-1 text-sm leading-relaxed text-emerald-900">
                  &ldquo;{prompt.exemplar}&rdquo;
                </p>
              </div>
            )}
          </>
        )}

        {!loading && result && working && (
          <div className="space-y-4">
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <p className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                Your version
              </p>
              <p className="mt-1 leading-relaxed text-gray-900">{answer}</p>
            </div>

            <EvalResultView data={result} accent="emerald" />

            {passed ? (
              isLast ? (
                <SignupGate
                  slug={config.slug}
                  heading="You've got the technique — nicely done."
                  blurb="Create a free account to get the full guided course — every tier and worked example — plus all the other tools and your saved progress."
                />
              ) : (
                <button
                  onClick={nextPrompt}
                  className="w-full rounded-xl bg-emerald-500 px-5 py-3 text-base font-bold text-white hover:bg-emerald-600"
                >
                  Nice — next prompt →
                </button>
              )
            ) : (
              <button
                onClick={tryAgain}
                className="w-full rounded-xl bg-emerald-600 px-5 py-3 text-base font-bold text-white hover:bg-emerald-700"
              >
                Not yet — try this one again
              </button>
            )}
          </div>
        )}

        {gated && (
          <SignupGate
            slug={config.slug}
            heading="You're on a roll — keep going free"
            blurb="That's your free practice for now. Create a free account to keep practising, unlock the full guided course, and save your progress across every tool."
          />
        )}

        {rateLimited && (
          <div className="rounded-2xl border border-line bg-paper-2 p-6 text-center shadow-[0_4px_0_var(--gold)]">
            <p className="text-3xl">⏳</p>
            <h3 className="font-display mt-2 text-xl font-semibold">Whoa — slow down a moment</h3>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-ink-soft">
              That&rsquo;s a lot of tries in a short time. Wait a minute and try again — or create a
              free account to keep practising without the wait.
            </p>
            <div className="mt-5 flex items-center justify-center gap-3">
              <Button variant="secondary" size="sm" onClick={tryAgain}>
                Try again
              </Button>
              <Button href={`/auth/signup?from=${config.slug}`} size="sm">
                Create a free account →
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
